import { Briefcase, Hourglass, RefreshCw } from "lucide-mui";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import DistributionChart from "../components/DistributionChart.tsx";
import FormattedDate from "../components/FormattedDate.tsx";
import FilterBar from "../components/filters/FilterBar.tsx";
import SavedViewsMenu from "../components/filters/SavedViewsMenu.tsx";
import type { FilterSpec } from "../components/filters/specs.ts";
import { useFilters } from "../components/filters/useFilters.ts";
import KpiTile from "../components/KpiTile.tsx";
import { StatusLabel } from "../components/StatusIcon.tsx";
import { CheckCell, TitleLinkCell } from "../components/table/cells.tsx";
import EmptyTableRow from "../components/table/EmptyTableRow.tsx";
import ScrollTable from "../components/table/ScrollTable.tsx";
import SortableTableHead from "../components/table/SortableTableHead.tsx";
import TableCard from "../components/table/TableCard.tsx";
import UrlTablePagination from "../components/table/UrlTablePagination.tsx";
import { useOrganization } from "../contexts/OrganizationContext.tsx";
import { useSourceStats } from "../hooks/useSourceStats.ts";
import { useCommaParam, useUrlPaging, useUrlSort, useUrlState } from "../hooks/useUrlState.ts";
import { booleanFilterToParam, parseBooleanFilter } from "../utils/urlFilters.ts";
import { formatNumber } from "../utils/format.ts";
import PageHeader from "../components/PageHeader.tsx";

const COLUMNS = [
  { id: "domain", label: "Domain" },
  { id: "ats", label: "ATS" },
  { id: null, label: "Subdomain" },
  { id: null, label: "Verified" },
  { id: null, label: "HTTP status" },
  { id: null, label: "Discovery source" },
  { id: "last_checked_at", label: "Last checked" },
  { id: "last_crawled_at", label: "Last crawled" },
  { id: "created_at", label: "Created" },
  { id: null, label: "Jobs" },
  { id: null, label: "Error" },
];

function AtsFeedsPage() {
  const { currentOrg } = useOrganization();
  const url = useUrlState();
  const { searchParams } = url;

  const { data: filterOptions } = useQuery({
    queryKey: keys.atsFeed.filterOptions(),
    queryFn: () => api.getAtsFeedFilterOptions(),
    staleTime: 30 * 60 * 1000,
  });

  const specs = useMemo<FilterSpec[]>(() => {
    const loading = !filterOptions;
    return [
      {
        kind: "options",
        key: "ats",
        label: "ATS provider",
        param: "ats",
        pinned: true,
        loading,
        options: (filterOptions?.ats_providers ?? []).map((a) => ({ value: a, label: a })),
      },
      {
        kind: "options",
        key: "discovery_source",
        label: "Discovery source",
        param: "discovery_source",
        loading,
        options: (filterOptions?.discovery_sources ?? []).map((ds) => ({ value: ds, label: ds })),
      },
      { kind: "boolean", key: "verified", label: "Verified", param: "verified" },
      { kind: "boolean", key: "is_subdomain", label: "Subdomain", param: "is_subdomain" },
    ];
  }, [filterOptions]);

  const filters = useFilters(specs, {
    state: url,
    search: { param: "domain", placeholder: "Search domains" },
    pageKey: "ats-feeds",
    scopeId: null,
    remember: true,
  });

  const atsProviders = useCommaParam(searchParams, "ats");
  const discoverySources = useCommaParam(searchParams, "discovery_source");
  const yesNo = (key: string) => booleanFilterToParam(parseBooleanFilter(searchParams.get(key)));

  const { sortBy, sortDir, handleSort } = useUrlSort(url, "domain", "asc");
  const paging = useUrlPaging(url);

  const queryParams = {
    domain: searchParams.get("domain") || undefined,
    ats: atsProviders.length ? atsProviders : undefined,
    discovery_source: discoverySources.length ? discoverySources : undefined,
    verified: yesNo("verified"),
    is_subdomain: yesNo("is_subdomain"),
    sort_by: sortBy,
    sort_dir: sortDir,
    limit: paging.rowsPerPage,
    offset: paging.offset,
  };

  const { data, isLoading } = useQuery({
    queryKey: keys.atsFeed.list(queryParams),
    queryFn: () => api.getAtsFeeds(queryParams),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const { data: crawlStats } = useQuery({
    queryKey: keys.atsFeed.crawlStats(),
    queryFn: () => api.getAtsFeedCrawlStats(),
    staleTime: 60_000,
  });

  const stats = useSourceStats(keys.atsFeed.stats(), api.getAtsFeedStats, (s) => ({
    label: s.ats,
    count: s.count,
    failedCount: s.failed_count,
  }));

  const crawledShare =
    crawlStats && crawlStats.total_verified > 0
      ? (crawlStats.crawled_last_24h / crawlStats.total_verified) * 100
      : 0;

  return (
    <>
      <PageHeader
        title="ATS feeds"
        description="Job boards found on company websites, crawled daily for job ads"
      />
      {crawlStats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile
              dense
              label="Crawled last 24h"
              icon={<RefreshCw />}
              value={crawlStats.crawled_last_24h}
              caption={`of ${formatNumber(crawlStats.total_verified)} verified feeds`}
            >
              <LinearProgress variant="determinate" value={crawledShare} sx={{ height: 6 }} />
            </KpiTile>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile
              dense
              label="Lag"
              icon={<Hourglass />}
              color={crawlStats.lag > 0 ? "warning" : "success"}
              value={crawlStats.lag}
              caption={
                crawlStats.lag > 0 ? (
                  <StatusLabel kind="attention" label="Verified feeds not crawled in 24h" />
                ) : (
                  <StatusLabel kind="ok" label="Every verified feed crawled" />
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <KpiTile
              dense
              label="Jobs discovered"
              icon={<Briefcase />}
              value={crawlStats.total_jobs_last_24h}
              caption="Last 24 hours"
            />
          </Grid>
        </Grid>
      )}
      <DistributionChart
        title="Feeds per ATS"
        data={stats.chartData}
        loading={stats.isLoading}
      />
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={data ? `${formatNumber(data.total)} ${data.total === 1 ? "feed" : "feeds"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filters}
                context={{ organizationId: currentOrg?.id, organizationName: currentOrg?.name }}
              />
            }
          />
        }
        footer={data && data.total > 0 && <UrlTablePagination total={data.total} paging={paging} />}
      >
        <ScrollTable>
          <SortableTableHead
            columns={COLUMNS}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            defaultDir="asc"
          />
          <TableBody>
            {data?.items.map((feed) => (
              <TableRow key={feed.id} hover>
                <TitleLinkCell title={feed.domain} href={feed.feed_url} />
                <TableCell>{feed.ats}</TableCell>
                <CheckCell checked={feed.is_subdomain} label="Subdomain" />
                <CheckCell checked={feed.verified} label="Verified" onFalse="cross" />
                <TableCell>{feed.http_status ?? "-"}</TableCell>
                <TableCell>{feed.discovery_source}</TableCell>
                <TableCell>
                  <FormattedDate value={feed.last_checked_at} />
                </TableCell>
                <TableCell>
                  <FormattedDate value={feed.last_crawled_at} />
                </TableCell>
                <TableCell>
                  <FormattedDate value={feed.created_at} />
                </TableCell>
                <TableCell>{formatNumber(feed.job_count)}</TableCell>
                <TableCell>
                  {feed.error ? (
                    <Tooltip title={feed.error}>
                      <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 200 }}>
                        {feed.error}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      -
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {data?.items.length === 0 && (
              <EmptyTableRow colSpan={COLUMNS.length}>
                {filters.activeCount > 0 ? "No ATS feeds match your filters" : "No ATS feeds found"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default AtsFeedsPage;
